import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  applyNodeChanges,
  type Connection,
  type Node,
  type NodeChange,
  type NodeProps,
} from '@xyflow/react';
import { useMemo, useState } from 'react';

import { ONBOARDING_CASE_STAGE_LABEL } from '@/products/onboarding/features/onboarding-template-labels';
import {
  FLOW_CARD_H,
  FLOW_CARD_W,
  frameSize,
  linkTasks,
  moveTask,
  taskShape,
  type FlowPhase,
} from '@/products/onboarding/features/onboarding-flow-layout';
import type { OnboardingTaskStatus } from '@gb-ticket/shared-types';

import '@xyflow/react/dist/style.css';

export type FlowSelection = { type: 'phase'; id: string } | { type: 'task'; key: string } | null;

type PhaseData = { title: string; stage: FlowPhase['stage'] };
type TaskData = {
  title: string;
  kind: FlowPhase['tasks'][number]['kind'];
  shape: string;
  status?: OnboardingTaskStatus;
};

const nodeTypes = {
  phase: PhaseNode,
  task: TaskNode,
};

export function OnboardingFlowCanvas(props: {
  phases: FlowPhase[];
  editable: boolean;
  statuses?: Readonly<Record<string, OnboardingTaskStatus | undefined>>;
  onPhasesChange: (phases: FlowPhase[]) => void;
  onSelect: (selection: FlowSelection) => void;
  onReject: (message: string) => void;
}) {
  const nodes = useMemo(
    () => toNodes(props.phases, props.statuses ?? {}),
    [props.phases, props.statuses],
  );
  const edges = useMemo(() => toEdges(props.phases), [props.phases]);
  const phaseKey = props.phases
    .map(
      (phase) =>
        `${phase.id}:${phase.x}:${phase.y}:${phase.tasks.map((task) => `${task.key}:${task.x}:${task.y}:${task.dependsOn.join('+')}`).join(',')}`,
    )
    .join('|');
  const [seen, setSeen] = useState(phaseKey);
  const [dragNodes, setDragNodes] = useState<Node[] | null>(null);
  if (seen !== phaseKey) {
    setSeen(phaseKey);
    setDragNodes(null);
  }
  const live = dragNodes ?? nodes;

  function onNodesChange(changes: NodeChange<Node>[]) {
    setDragNodes((current) => applyNodeChanges(changes, current ?? nodes));
  }

  function commitDrag(node: Node) {
    if (node.type === 'phase') {
      props.onPhasesChange(
        props.phases.map((phase) =>
          phase.id === node.id ? { ...phase, x: node.position.x, y: node.position.y } : phase,
        ),
      );
      setDragNodes(null);
      return;
    }
    const key = node.id.slice('task:'.length);
    const phase = props.phases.find((item) => item.tasks.some((task) => task.key === key));
    if (!phase) {
      return;
    }
    const parent = live.find((item) => item.id === phase.id);
    const originX = parent?.position.x ?? phase.x;
    const originY = parent?.position.y ?? phase.y;
    props.onPhasesChange(
      moveTask(props.phases, key, originX + node.position.x, originY + node.position.y),
    );
    setDragNodes(null);
  }

  function onConnect(connection: Connection) {
    if (!props.editable || !connection.source || !connection.target) {
      return;
    }
    const source = connection.source.slice('task:'.length);
    const target = connection.target.slice('task:'.length);
    const linked = linkTasks(props.phases, source, target);
    if (!linked.ok) {
      props.onReject(linked.message);
      return;
    }
    props.onPhasesChange(linked.phases);
  }

  return (
    <div
      className="h-[min(70dvh,28rem)] overflow-hidden rounded-xl border border-border bg-card shadow-sm sm:h-[640px]"
      data-editable={props.editable ? 'true' : 'false'}
    >
      <ReactFlow
        nodes={live}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onNodeDragStop={(_event, node) => commitDrag(node)}
        onNodeClick={(_event, node) => {
          if (node.type === 'phase') {
            props.onSelect({ type: 'phase', id: node.id });
            return;
          }
          props.onSelect({ type: 'task', key: node.id.slice('task:'.length) });
        }}
        onConnect={onConnect}
        nodesDraggable={props.editable}
        nodesConnectable={props.editable}
        elementsSelectable
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.2}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

function PhaseNode({ data }: NodeProps) {
  const phase = data as PhaseData;
  const stageLabel = ONBOARDING_CASE_STAGE_LABEL[phase.stage];
  return (
    <div
      className="h-full rounded-md border border-border bg-card shadow-sm"
      aria-label={phase.title}
    >
      <div className="flow-phase-handle flex h-10 cursor-grab items-center gap-2 border-b bg-muted/60 px-3">
        <h2 className="truncate text-sm font-semibold">{phase.title}</h2>
        {stageLabel === phase.title ? null : (
          <span className="rounded-full bg-background px-2 py-0.5 text-xs font-normal text-muted-foreground">
            {stageLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function TaskNode({ data }: NodeProps) {
  const task = data as TaskData;
  return (
    <div
      className={cardClass(task.kind, task.shape, task.status)}
      aria-label={task.title}
      data-task-kind={task.kind}
      data-shape={task.shape}
      data-status={task.status ?? 'none'}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!size-3 !border-zinc-500 !bg-white"
      />
      <ShapeGlyph shape={task.shape} />
      <span className="line-clamp-2 min-w-0 flex-1 text-sm font-medium">{task.title}</span>
      <StatusMark status={task.status} />
      <Handle
        type="source"
        position={Position.Right}
        className="!size-3 !border-zinc-500 !bg-white"
      />
    </div>
  );
}

function StatusMark(props: { status?: OnboardingTaskStatus }) {
  if (props.status === 'completed') {
    return <span aria-hidden>✓</span>;
  }
  if (props.status === 'skipped' || props.status === 'cancelled') {
    return <span aria-hidden>—</span>;
  }
  return null;
}

function ShapeGlyph(props: { shape: string }) {
  const { shape } = props;
  if (shape === 'lines') {
    return (
      <svg aria-hidden viewBox="0 0 16 16" className="size-4 shrink-0">
        <path d="M2 4h12M2 8h12M2 12h8" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    );
  }
  if (shape === 'check') {
    return (
      <svg aria-hidden viewBox="0 0 16 16" className="size-4 shrink-0">
        <path d="M3 8.5 6.5 12 13 4" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    );
  }
  const inward = shape.endsWith('-in');
  const outward = shape.endsWith('-out');
  return (
    <>
      {shape === 'document-out' || shape === 'document-in' ? (
        <span
          aria-hidden
          className="pointer-events-none absolute top-0 right-0 size-3 border-b border-l border-current bg-white"
        />
      ) : null}
      {inward || outward ? <DirectionArrow inward={inward} /> : null}
    </>
  );
}

function DirectionArrow(props: { inward: boolean }) {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-4 shrink-0">
      <path
        d={props.inward ? 'M11 3 5 8l6 5M5 8h8' : 'M5 3l6 5-6 5M3 8h8'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function cardClass(kind: TaskData['kind'], shape: string, status?: OnboardingTaskStatus): string {
  const color =
    kind === 'data' || kind === 'review'
      ? 'border-sky-500 text-sky-950'
      : kind === 'document_provide' || kind === 'document_upload'
        ? 'border-amber-500 text-amber-950'
        : kind === 'asset_issue' || kind === 'asset_return'
          ? 'border-emerald-600 text-emerald-950'
          : kind === 'vehicle_assign' || kind === 'vehicle_return'
            ? 'border-teal-600 text-teal-950'
            : 'border-zinc-500 text-zinc-900';
  const fill =
    shape === 'outline'
      ? 'bg-card'
      : kind === 'data' || kind === 'review'
        ? 'bg-sky-100'
        : kind === 'document_provide' || kind === 'document_upload'
          ? 'bg-amber-100'
          : kind === 'asset_issue' || kind === 'asset_return'
            ? 'bg-emerald-100'
            : kind === 'vehicle_assign' || kind === 'vehicle_return'
              ? 'bg-teal-100'
              : 'bg-zinc-200';
  const radius =
    shape === 'square-out' || shape === 'square-in'
      ? 'rounded-none'
      : shape === 'pill-out' || shape === 'pill-in'
        ? 'rounded-full'
        : 'rounded-md';
  const weight = shape === 'double' ? 'border-4' : 'border';
  const dim = status === 'not_applicable' || status === 'cancelled' ? 'opacity-40' : '';
  return `relative flex h-full w-full items-center gap-2 px-3 ${color} ${fill} ${radius} ${weight} ${dim}`;
}

function toNodes(
  phases: readonly FlowPhase[],
  statuses: Readonly<Record<string, OnboardingTaskStatus | undefined>>,
): Node[] {
  const nodes: Node[] = [];
  for (const phase of phases) {
    const size = frameSize(phase.tasks);
    nodes.push({
      id: phase.id,
      type: 'phase',
      position: { x: phase.x, y: phase.y },
      data: { title: phase.title, stage: phase.stage } satisfies PhaseData,
      style: { width: size.width, height: size.height },
      dragHandle: '.flow-phase-handle',
      selectable: true,
    });
    for (const task of phase.tasks) {
      nodes.push({
        id: `task:${task.key}`,
        type: 'task',
        parentId: phase.id,
        position: { x: task.x, y: task.y },
        data: {
          title: task.title,
          kind: task.kind,
          shape: taskShape(task.kind),
          status: statuses[task.key],
        } satisfies TaskData,
        style: { width: FLOW_CARD_W, height: FLOW_CARD_H },
      });
    }
  }
  return nodes;
}

function toEdges(phases: readonly FlowPhase[]) {
  const edges = [];
  for (const phase of phases) {
    for (const task of phase.tasks) {
      for (const source of task.dependsOn) {
        edges.push({
          id: `${source}->${task.key}`,
          source: `task:${source}`,
          target: `task:${task.key}`,
          style: { stroke: '#a1a1aa' },
        });
      }
    }
  }
  return edges;
}
